import { Box, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { apiGet } from '../api.js';

const formatWeight = (value) =>
  Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const parseWeight = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const n = parseFloat(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const sumKeptWeight = (articles = []) => {
  const total = articles
    .filter((article) => article && article.released !== true)
    .reduce((sum, article) => sum + parseWeight(article.weight), 0);
  return Math.round(total * 1000) / 1000;
};

const sumInterestReceived = (articles = []) => {
  const total = articles.reduce((sum, article) => {
    const paid = (article.payments || [])
      .filter((p) => p.type === 'interest')
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const atRelease = article.released ? (Number(article.releaseInterest) || 0) : 0;
    return sum + paid + atRelease;
  }, 0);
  return Math.round(total * 100) / 100;
};

const StatCard = ({ label, value }) => (
  <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
    <Text fontSize="sm" color="gray.500">{label}</Text>
    <Heading size="lg">{value}</Heading>
  </Box>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiGet('/dashboard');
        let totalWeight = result.totalWeight;
        let totalInterestReceived = result.totalInterestReceived;
        const needArticles = typeof totalWeight !== 'number' || totalWeight === 0
          || typeof totalInterestReceived !== 'number';
        if (needArticles) {
          const articleResult = await apiGet('/articles');
          if (typeof totalWeight !== 'number' || totalWeight === 0) {
            totalWeight = sumKeptWeight(articleResult.articles);
          }
          if (typeof totalInterestReceived !== 'number') {
            totalInterestReceived = sumInterestReceived(articleResult.articles);
          }
        }
        setData({ ...result, totalWeight, totalInterestReceived });
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
      }
    };
    fetchData();
  }, []);

  return (
    <Stack spacing={6}>
      <Box>
        <Heading size="lg">Dashboard</Heading>
        <Text color="gray.500">Overview of your business</Text>
      </Box>
      {error && <Text color="red.500">{error}</Text>}
      {data && (
        <>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <StatCard label="Total Clients" value={data.totalClients} />
            <StatCard label="Total Articles" value={data.totalArticles} />
            <StatCard label="Active Articles" value={data.activeArticles} />
            <StatCard label="Released Articles" value={data.releasedArticles} />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <StatCard
              label="Total Weight (kept articles only)"
              value={`${formatWeight(data.totalWeight)} g`}
            />
            <StatCard
              label="Interest received (payments + released)"
              value={formatMoney(data.totalInterestReceived)}
            />
          </SimpleGrid>
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
              <Heading size="md" mb={4}>Recent Articles</Heading>
              {data.recentArticles?.length ? (
                <Stack spacing={3}>
                  {data.recentArticles.map(article => (
                    <Box key={article._id} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                      <Text fontWeight="semibold">{article.client?.name || 'N/A'}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {article.nameOrNo || 'N/A'} • ₹{article.amount || 0}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Text color="gray.500">No recent articles found.</Text>
              )}
          </Box>
        </>
      )}
    </Stack>
  );
};

export default Dashboard;
